import { version } from '@root/package.json'
import * as Sentry from '@sentry/react'
import axios, { HttpStatusCode } from 'axios'
import { v4 } from 'uuid'

import { AuthService } from '@/api/models/auth'
import { useServiceErrorsStore } from '@/components/entities/common/model/service-errors.store'
import { getUserAgentData } from '@/utils/getUserAgentData'

import { INVALID_GRANT_ERROR_CODE } from '../constants/errors'
import { refreshTokenStrategy } from './refreshTokenStrategy'

axios.interceptors.request.use(config => {
  const { osName, osVersion, deviceModel } = getUserAgentData(
    navigator.userAgent
  )
  config.headers['X-Sc-Platform'] = 'web-client'
  config.headers['X-Sc-Os-Name'] = osName
  config.headers['X-Sc-Os-Version'] = osVersion
  config.headers['X-Sc-Device-Model'] = deviceModel
  config.headers['X-SC-App-Version'] = version
  config.headers['X-Sc-Timezone'] =
    Intl.DateTimeFormat().resolvedOptions().timeZone
  config.headers['X-Correlation-Id'] = v4()

  return config
})

axios.interceptors.request.use(config => {
  const { accessToken } = AuthService.getTokens()
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`
  }
  return config
})

axios.interceptors.response.use(
  ok => ok,
  async error => {
    const originalRequest = error.config
    const endpoint = originalRequest.url.split('/').slice(-2).join('/')

    if (
      error.response?.status === HttpStatusCode.Unauthorized &&
      endpoint !== 'auth/token' &&
      window.location.pathname !== '/auth/invite'
    ) {
      return refreshTokenStrategy(originalRequest)
    }

    throw error
  }
)

axios.interceptors.response.use(null, error => {
  if (error.response) {
    const { status, data, config } = error.response

    if (status >= 400 && status < 599) {
      if (status === HttpStatusCode.ServiceUnavailable) {
        useServiceErrorsStore.getState().setDown(data)
      } else if (status === HttpStatusCode.PayloadTooLarge) {
        useServiceErrorsStore.getState().setIsContentLarge(true)
      } else if (
        status === HttpStatusCode.Unauthorized &&
        data.error !== INVALID_GRANT_ERROR_CODE &&
        window.location.pathname !== '/auth/invite'
      ) {
        throw error
      } else {
        Sentry.captureException(error, {
          contexts: {
            response: {
              status,
              data: JSON.stringify(data),
              url: config.url,
              method: config.method
            }
          },
          tags: {
            axios_error: 'true',
            status_code: status
          }
        })
      }
    }
    throw error
  }
})

axios.defaults.paramsSerializer = {
  indexes: null
}
